import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ConversationSettings.module.scss';
import Input from 'component/atom/forms/input/input/Input';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import ColorSelector from './components/color-selector/ColorSelector';
import ThemeSelector from './components/theme-selector/ThemeSelector';
import TextSelector from './components/text-selector/TextSelector';
import StyleSelector from './components/style-selector/StyleSelector';
import Select from 'component/atom/forms/select/select/Select';
import { useFormikContext } from 'formik';
import headerStyle from '../../../../../../assets/image/header-style.png';
import headerAlignment from '../../../../../../assets/image/header-alignment.png';
import Bot from '../ConversationCard/bot';
import toast from 'react-hot-toast';

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp';

const ConversationSettings = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const fileInputRef = useRef(null);
  const chatIconSrc = useMemo(() => {
    const raw = values?.ChatIcon;

    const typeHintRaw = values?.IconType || '';
    const typeHint =
      String(typeHintRaw).toLowerCase() === 'image/jpg'
        ? 'image/jpeg'
        : String(typeHintRaw).toLowerCase();

    if (raw === 'default') return 'default';
    if (!raw) return null;

    const normB64 = (s) => {
      const t = String(s)
        .trim()
        .replace(/\s+/g, '')
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      return t + '='.repeat((4 - (t.length % 4)) % 4);
    };

    const allow = (mime) =>
      mime === 'image/svg+xml' ||
      mime === 'image/png' ||
      mime === 'image/jpeg' ||
      mime === 'image/jpg';

    if (typeof raw === 'string') {
      const s = raw.trim();

      if (/^data:image\//i.test(s)) {
        const m = s.match(/^data:(image\/[a-z0-9+.-]+)[;,]/i);
        const mime = m?.[1]?.toLowerCase() || '';
        return allow(mime) ? s : null;
      }

      if (/^https?:\/\//i.test(s) || s.startsWith('/')) {
        if (/\.(svg|png)(\?|#|$)/i.test(s)) return s;
        return null;
      }

      if (s.startsWith('<svg')) {
        return `data:image/svg+xml;utf8,${encodeURIComponent(s)}`;
      }

      const b64 = normB64(s);
      let mime = '';

      if (allow(typeHint)) mime = typeHint;

      if (!mime) {
        if (b64.startsWith('PHN2Zy')) mime = 'image/svg+xml';
        else if (b64.startsWith('iVBORw0KGgo')) mime = 'image/png';
      }

      if (!mime) return null;

      const flavor = mime === 'image/svg+xml' ? 'image/svg+xml' : 'image/png';
      return `data:${flavor};base64,${b64}`;
    }

    if (raw instanceof Blob) {
      const mime = raw.type?.toLowerCase() || '';
      return allow(mime) ? URL.createObjectURL(raw) : null;
    }

    return null;
  }, [values?.ChatIcon, values?.IconType]);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFieldValue('ChatIcon', file);
    setFieldValue('IconType', file.type);
  };

  return (
    <div className={styles['page-settings-card']}>
      <div className={styles['bot-title-settings']}>
        <div
          className={`${styles['input-item-container']} ${styles['title-input']}`}
        >
          <p className={styles['input-title']}>Bot Title</p>
          <Input
            value={values?.ChatbotName}
            label='Chatbot Name'
            maxLength={30}
            inputClassname={styles['input']}
            name='ChatbotName'
            onChange={(e) => setFieldValue('ChatbotName', e.target.value)}
            error={errors?.ChatbotName}
            countSpaces
            touched={touched?.ChatbotName}
          />
        </div>

        <div className={styles['input-item-container']}>
          <div className={styles['input-title-container']}>
            <p className={styles['input-title']}>Chat Icon</p>
            {values?.ShowChatIcon ? (
              <SvgWithBranding
                type='eyeBranding'
                color='#9E9E9E'
                className={styles['eye-icon']}
                onClick={() =>
                  setFieldValue('ShowChatIcon', !values.ShowChatIcon)
                }
              />
            ) : (
              <SvgWithBranding
                type='eyeBrandingStripped'
                color='#9E9E9E'
                className={styles['eye-icon']}
                onClick={() =>
                  setFieldValue('ShowChatIcon', !values.ShowChatIcon)
                }
              />
            )}
          </div>

          <div className={styles['input-card']}>
            <div className={styles['iconWrap']}>
              {chatIconSrc !== 'default' ? (
                <img
                  src={chatIconSrc || '/src/svg/bot.svg'}
                  alt='chat-icon'
                  className={styles['bot-icon-blue']}
                />
              ) : (
                <Bot width={30} height={30} />
              )}
            </div>

            <div
              className={styles['upload-container']}
              role='button'
              tabIndex={0}
              onClick={handlePickFile}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && handlePickFile()
              }
            >
              <SvgWithBranding type='uploadBranding' color='#1A1A1A' />
            </div>

            <input
              ref={fileInputRef}
              type='file'
              accept={ACCEPT}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        </div>

        <ColorSelector
          title='Icon Background'
          colors={['#BFD6FA', '#B9DBFA', '#D1BFFB']}
          includePicker
          fieldName='IconBackground'
          value={values.IconBackground}
          setFieldValue={setFieldValue}
          onEyeClick={() =>
            setFieldValue('ShowIconBackground', !values.ShowIconBackground)
          }
          viewToggled={values.ShowIconBackground}
        />
      </div>

      <div className={styles['theme-settings']}>
        <ThemeSelector values={values} setFieldValue={setFieldValue} />
        <TextSelector
          title='Header Style'
          fieldName='HeaderStyle'
          options={[
            { label: 'Text', position: 'center' },
            { label: 'Icon + Text', position: 'center' },
          ]}
          values={values}
          setFieldValue={setFieldValue}
          tooltipContent={
            <div className={styles['tooltip-content']}>
              <p>The heading style is the top part of the chatbot.</p>
              <img src={headerStyle} alt='header-style' />
            </div>
          }
        />
        <TextSelector
          title='Title Position'
          fieldName='TitlePosition'
          options={[
            { label: 'Left', position: 'left' },
            { label: 'Center', position: 'center' },
            { label: 'Right', position: 'right' },
          ]}
          values={values}
          setFieldValue={setFieldValue}
          tooltipContent={
            <div className={styles['tooltip-content']}>
              <p>
                Set the alignment of your bot title — choose from left, center,
                or right to match your design.
              </p>
              <img src={headerAlignment} alt='header-alignment' width={366} />
            </div>
          }
        />
      </div>

      <div
        className={`${styles['theme-settings']} ${styles['color-pickers-container']}`}
      >
        <StyleSelector
          title='Style'
          value={values.Style}
          onChange={(val) => setFieldValue('Style', val)}
        />
        <ColorSelector
          title='Accent Color'
          colors={['#0957D0', '#128DEA', '#7C3AED', '#0A7E1D', '#FF9706']}
          includePicker
          fieldName='AccentColor'
          value={values.AccentColor}
          setFieldValue={setFieldValue}
          onEyeClick={() => {}}
          hasView={false}
        />
        <ColorSelector
          title='Icon Colors'
          colors={[values.IconColorPrimary]}
          includePicker
          fieldName='IconColorPrimary'
          value={values.IconColorPrimary}
          setFieldValue={setFieldValue}
          // onEyeClick={() => {}}
          hasView={false}
        />
        <div className={styles['text-style-container']}>
          <p className={styles['input-title']}>Text Style</p>
          <Select
            label='Font Style'
            name='FontFamily'
            value={values.FontFamily}
            onChange={(e) => setFieldValue('FontFamily', e.target.value)}
            placeholder='Choose a font'
            options={[
              { value: 'Helvetica Neue', label: 'Helvetica Neue' },
              { value: 'Montserrat', label: 'Montserrat' },
              { value: 'Roboto', label: 'Roboto' },
              { value: 'Raleway', label: 'Raleway' },
              { value: 'Poppins', label: 'Poppins' },
            ]}
          />
        </div>
        <ColorSelector
          title='Header Font Color'
          colors={[values.HeaderFontColor]}
          includePicker
          fieldName='HeaderFontColor'
          value={values.HeaderFontColor}
          setFieldValue={setFieldValue}
          // onEyeClick={() => {}}
          hasView={false}
        />
      </div>

      <div className={styles['message-area']}>
        <p className={styles['input-title']}>Message Area</p>
        <Input
          value={values?.PlaceholderText}
          label='Placeholder Text'
          inputContainerClassName={styles['message-area-input']}
          maxLength={80}
          inputClassname={styles['input']}
          name='PlaceholderText'
          onChange={(e) => setFieldValue('PlaceholderText', e.target.value)}
          countSpaces={true}
          error={errors?.PlaceholderText}
          touched={touched?.PlaceholderText}
        />
      </div>
    </div>
  );
};

export default ConversationSettings;
